import * as React from 'react'
import { Search } from 'lucide-react'
import { Input } from '@/components/ui/input'
import { cn } from '@/lib/utils'

type CommandContextValue = {
  query: string
  setQuery: (value: string) => void
}

const CommandContext = React.createContext<CommandContextValue | null>(null)

function useCommandContext() {
  const context = React.useContext(CommandContext)

  if (!context) {
    throw new Error('Command components must be used within Command')
  }

  return context
}

interface CommandProps extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode
}

const Command = React.forwardRef<HTMLDivElement, CommandProps>(
  ({ className, children, ...props }, ref) => {
    const [query, setQuery] = React.useState('')

    return (
      <CommandContext.Provider value={{ query, setQuery }}>
        <div
          ref={ref}
          className={cn('flex h-full w-full flex-col overflow-hidden rounded-md bg-popover', className)}
          {...props}
        >
          {children}
        </div>
      </CommandContext.Provider>
    )
  }
)
Command.displayName = 'Command'

const CommandInput = React.forwardRef<
  HTMLInputElement,
  React.ComponentPropsWithoutRef<typeof Input>
>(({ className, onChange, ...props }, ref) => {
  const { query, setQuery } = useCommandContext()

  return (
    <div className="flex items-center border-b px-3">
      <Search className="mr-2 h-4 w-4 shrink-0 text-muted-foreground" />
      <Input
        ref={ref}
        value={query}
        onChange={(event) => {
          setQuery(event.target.value)
          onChange?.(event)
        }}
        className={cn('border-0 px-0 shadow-none focus-visible:ring-0', className)}
        {...props}
      />
    </div>
  )
})
CommandInput.displayName = 'CommandInput'

const CommandList = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn('max-h-64 overflow-y-auto overflow-x-hidden', className)} {...props} />
  )
)
CommandList.displayName = 'CommandList'

const CommandEmpty = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn('py-6 text-center text-sm text-muted-foreground', className)} {...props} />
  )
)
CommandEmpty.displayName = 'CommandEmpty'

const CommandGroup = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn('p-1', className)} {...props} />
  )
)
CommandGroup.displayName = 'CommandGroup'

const CommandSeparator = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn('-mx-1 my-1 h-px bg-border', className)} {...props} />
  )
)
CommandSeparator.displayName = 'CommandSeparator'

interface CommandItemProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  inset?: boolean
}

const CommandItem = React.forwardRef<HTMLButtonElement, CommandItemProps>(
  ({ className, type = 'button', ...props }, ref) => (
    <button
      ref={ref}
      type={type}
      className={cn(
        'relative flex w-full cursor-default select-none items-center justify-start text-left rounded-sm px-2 py-1.5 text-sm outline-none transition-colors hover:bg-accent hover:text-accent-foreground',
        className
      )}
      {...props}
    />
  )
)
CommandItem.displayName = 'CommandItem'

export {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
  useCommandContext,
}
